import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { loginPageModuleQuery } from '@data/sanity/queries/page'
import { SanityLoginPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPageSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getLoginPage } from '@lib/sanity/page'
import { Locale } from '@lib/language'
import { CustomStaticPropsContext } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface LoginPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  page: SanityLoginPage
  site: SanitySiteFragment
}

const LoginPage = ({
  draftMode,
  draftToken,
  locale,
  page,
  site,
}: LoginPageProps) => {
  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={loginPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<LoginPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const loginPage = await getLoginPage(sanityClient, context.locale)

  if (!loginPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      page: loginPage.page,
      site: loginPage.site,
    },
    revalidate: 60,
  }
}

export default LoginPage
